/**
 * Чистая логика очереди правок товаров (ARCH-TASK-64a.1).
 *
 * Без импортов (ни Dexie, ни React) — чтобы её можно было покрыть тестами
 * в node:test и переиспользовать в синке.
 */
export interface ProductEditFields {
  price?: number
  minStock?: number | null
  name?: string
  barcode?: string
  category?: string
  categoryParent?: string
  requiresMarking?: boolean
}

export interface PendingProductEdit extends ProductEditFields {
  cloudId: number
  at: number
  attempts: number
}

/** Что реально отправлено в облако — удаляем только это. */
export type SentEdit = ProductEditFields & { cloudId: number }

/** Поля, которые умеет править админка. */
export const EDITABLE_FIELDS = [
  'price',
  'minStock',
  'name',
  'barcode',
  'category',
  'categoryParent',
  'requiresMarking'
] as const

/**
 * Слить новую правку с уже стоящей в очереди.
 *
 * Поля независимы: правка цены не стирает имя или категорию. `null` у
 * `minStock` — осознанный сброс, поэтому проверяем `!== undefined`,
 * а не истинность. `requiresMarking: false` — тоже валидное значение.
 */
export function mergePendingEdit(
  existing: PendingProductEdit | undefined,
  cloudId: number,
  patch: ProductEditFields,
  now: number
): PendingProductEdit {
  const merged = {
    cloudId,
    at: now,
    attempts: 0
  } as PendingProductEdit
  for (const field of EDITABLE_FIELDS) {
    const next = patch[field]
    const prev = existing?.[field]
    const value = next !== undefined ? next : prev
    if (value !== undefined) {
      ;(merged as unknown as Record<string, unknown>)[field] = value
    }
  }
  return merged
}

/** Совпадает ли запись очереди с отправленной правкой (по отправленным полям). */
export function matchesSentEdit(entry: PendingProductEdit, sent: SentEdit): boolean {
  if (entry.cloudId !== sent.cloudId) return false
  for (const field of EDITABLE_FIELDS) {
    const expected = sent[field]
    if (expected === undefined) continue
    const actual = entry[field] ?? null
    if ((actual ?? null) !== (expected ?? null)) return false
  }
  return true
}
